import React from "react";
import * as M from "../../Modal";
import { v4 as uuid } from "uuid";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import { T, TC } from "../../../Constants";

const MAX_YEARS = 25;

//#region Types
type Hover = Record<"row" | "col", number>;
type Category = T.Mission["nen2767"]["categories"][number];

export type GeneralProps = {
    /** Disallow any edit of the config */
    read_only?: boolean;
    /** The NEN data */
    data: T.Mission["nen2767"];
    /** Callback to update the NEN data */
    set_data: (data: Partial<T.Mission["nen2767"]>) => void;
}
//#endregion

//#region Styles & Constants
const styles: Record<"table" | "cell" | "cell_category", React.CSSProperties> = {
    cell: { padding: "0px" },
    cell_category: { padding: "0.5rem" },
    table: { borderWidth: "1px", borderColor: "grey" },
}
//#endregion

const General: React.FC<GeneralProps> = ({ set_data, ...props }) => {
    const lg = H.useLanguage();
    const to_select_ref = React.useRef<C.Form.NumFieldRef>(null);
    const from_select_ref = React.useRef<C.Form.NumFieldRef>(null);
    // State to track which cell is currently being hovered
    const [hovered_cell, set_hovered_cell] = React.useState<Hover>(null);
    // State for risk categories
    const categories = React.useMemo(() => props.data?.categories || [], [props.data.categories]);
    const set_categories = React.useCallback<React.Dispatch<React.SetStateAction<Category[]>>>(setter => {
        if (typeof setter === "function") set_data({ categories: setter(categories) });
        else set_data({ categories: setter });
    }, [categories, set_data]);

    const table_header = React.useMemo(() => <tr>
        <td style={styles.cell_category}>{lg.getStaticText(TC.NEN_RISK_MATRIX_CATEGORY_NAME)}</td>
        {[9, 8, 7, 6, 5, 4, 3, 2, 1].map(num => (
            <td key={`header-${num}`} style={styles.cell_category} className="text-center">{num}</td>
        ))}
    </tr>, [lg]);

    const rows = React.useMemo(() => ({
        remove: (index: number) => set_categories(p => p.filter((_, i) => i !== index)),
        add: () => set_categories(p => p.concat({ name: "New Category", start_level: 9, id: uuid(), remarques: {} })),
        change_name: (row: number, name: string) => set_categories(p => p.map((c, i) => i === row ? { ...c, name } : c)),
        change_level: (row: number, start_level: number) => set_categories(p => p.map((c, i) => i === row ? { ...c, start_level } : c)),
    }), [set_categories]);

    const set_dates = React.useCallback((type: keyof T.Mission["nen2767"]["year"], value: number) => {
        // Assess the new value of "from" or "to"
        const from = type === "from" ? value : props.data?.year?.from;
        const to = type === "to" ? value : props.data?.year?.to;
        // Check if the new value is a number and not NaN
        const ok_to = typeof to === "number" && !isNaN(to);
        const ok_from = typeof from === "number" && !isNaN(from);
        // User just edited the "from" year, but if it's after the "to" year, make them equal
        if (type === "from" && ok_from && ok_to && from > to) value = to;
        // User just edited the "to" year, but if it's before the "from" year, make them equal
        else if (type === "to" && ok_from && ok_to && to < from) value = from;

        const max_years = MAX_YEARS - 1;
        // // Limit the budget to 25 years
        if (ok_from && ok_to && to - from > max_years) {
            if (type === "from") value = to - max_years;
            else value = from + max_years;
            // Alert the user that there is a limit to the period
            M.renderAlert({ type: "warning", title: TC.NEN_PERIOD_INVALID_TITLE, message: { ref: TC.NEN_PERIOD_INVALID_MSG, template: MAX_YEARS } });
        }

        // If the forced value is the same as the current value, use the ref to set the search correctly
        const set_search = type === "from" ? from_select_ref.current?.single?.current?.set_value : to_select_ref.current?.single?.current?.set_value;
        if (value === props.data.year?.[type]) set_search(value);

        // Update the data
        set_data({ year: { ...props.data.year, [type]: value } });
    }, [props.data.year, set_data]);

    // Function to generate the priority cells
    const renderPriorityCells = React.useCallback((row_index: number, start_level: Category["start_level"]) => {
        const cells = [];
        const is_row_hovered = hovered_cell?.row === row_index;
        const allow_change = !props.read_only && (!is_row_hovered || (hovered_cell.col > 1 && hovered_cell.col < 9));

        // Loop through columns 9 to 1 (right to left in the table)
        for (let col = 9; col >= 1; col--) {
            let cell_color: T.ColorTypes;
            let style = { ...styles.cell };
            // Render the real cell color
            if (col === start_level) cell_color = "gold";
            else if (col === start_level - 1) cell_color = "warning";
            else if (col === start_level - 2) cell_color = "danger";
            // Render the hovered cell, and add padding to real cells that are not hovered
            if (is_row_hovered && allow_change) {
                let hovered_cell_color: T.ColorTypes;
                if (col === hovered_cell?.col + 1) hovered_cell_color = "gold";
                else if (col === hovered_cell?.col) hovered_cell_color = "warning";
                else if (col === hovered_cell?.col - 1) hovered_cell_color = "danger";

                // The cell is hovered, render it with potential new color
                if (hovered_cell_color) cell_color = hovered_cell_color;
                // The cell is not hovered, but is selected, add opacity
                else if (cell_color) style.opacity = "0.25";
                // Change the cursor to a pointer
                style.cursor = "pointer";
            }

            cells.push(<td
                style={style}
                key={`cell-${col}`}
                onMouseLeave={() => set_hovered_cell(null)}
                className={cell_color ? `bg-${cell_color}` : ""}
                onMouseEnter={() => set_hovered_cell({ row: row_index, col })}
                onClick={() => allow_change && rows.change_level(row_index, col + 1)}
            />);
        }

        return cells;
    }, [hovered_cell, props.read_only]);

    return <div className="mt-2">
        <C.Title level={4} className="mb-3" text={TC.NEN_RISK_MATRIX_TITLE} />
        <BS.Table className="fs-85" style={styles.table} bordered>
            <tbody>
                {table_header}

                {categories.map((category, index) => <tr key={`row-${index}`}>
                    <td style={styles.cell_category} className="w-50">
                        {props.read_only
                            ? category.name
                            : <C.Form.TextField
                                hideLabel
                                noBottomMargin
                                value={category.name}
                                onChange={value => rows.change_name(index, value)}
                                suffix={{
                                    size: "sm",
                                    icon: "trash-alt",
                                    variant: "danger",
                                    style: { display: "inline" },
                                    onClick: () => rows.remove(index),
                                }}
                            />}
                    </td>
                    {renderPriorityCells(index, category.start_level)}
                </tr>)}
            </tbody>
        </BS.Table>

        {!props.read_only && <C.Button className="mb-3" size="sm" icon="plus" onClick={rows.add} text={TC.NEN_RISK_ADD_LINE} />}

        <C.Title level={4} className="mb-3" text={TC.NEN_BUDGET_PLAN_RANGE} />
        <BS.Row className="mb-3">
            <BS.Col md={6} className="d-flex justify-content-between align-items-center">

                <C.Flex alignItems="center" justifyContent="between">
                    <C.Form.NumField
                        noBottomMargin
                        decimalLimit={0}
                        ref={from_select_ref}
                        disabled={props.read_only}
                        value={props.data?.year?.from}
                        onChange={value => set_dates("from", value)}
                    />
                    <i className="fa fa-arrow-right mx-2"></i>
                    <C.Form.NumField
                        noBottomMargin
                        decimalLimit={0}
                        ref={to_select_ref}
                        disabled={props.read_only}
                        value={props.data?.year?.to}
                        onChange={value => set_dates("to", value)}
                    />
                </C.Flex>
            </BS.Col>
        </BS.Row>
    </div>;
}

export default General;